import { ref, reactive, computed } from 'vue';
import axios from 'axios';

const SORT_COLUMN_MAP = {
  'name': 'name',
  'type': 'type',
  'provider_name': 'provider_name',
  'base_client_skill': 'base_client_skill',
  'external_id': 'external_id'
};

const HEADERS = [
  { text: 'Name', value: 'name', width: '180px' },
  { text: 'Type', value: 'type', width: '120px' },
  { text: 'Provider', value: 'provider_name', width: '130px' },
  { text: 'External ID', value: 'external_id', width: '180px' },
  { text: 'Descriptors', value: 'descriptor_langs', sortable: false, width: '120px' },
  { text: 'Proficiencies', value: 'proficiency_langs', sortable: false, width: '120px' },
  { text: 'Base Skill', value: 'base_client_skill', sortable: true, width: '90px' },
];

export function useBaseSkillsAdmin() {
  // 1. Create self with reactive state
  const self = {
    loading: ref(false),
    items: ref([]),
    totalItems: ref(0),
    types: ref([]),
    providers: ref([]),
    selected: ref([]),
    headers: HEADERS,

    currentPage: ref(1),
    pageSize: ref(50),
    sortBy: ref('name'),
    sortDesc: ref(false),

    searchText: ref(''),
    selectedProvider: ref(null),
    selectedType: ref([]),
    selectedBaseStatus: ref(null),
    selectedDescriptorStatus: ref(null),
    selectedProficiencyStatus: ref(null),

    detailDialog: reactive({
      show: false,
      loading: false,
      item: null,
      descriptors: [],
      proficiencies: []
    }),

    exporting: ref(false),
  };

  // 2. Computed properties on self
  self.hasSelection = computed(() => self.selected.value.length > 0);

  self.selectionCount = computed(() => self.selected.value.length);

  // 3. Private helpers
  function _buildFilterParams() {
    return {
      search: self.searchText.value || undefined,
      framework_provider_id: self.selectedProvider.value || undefined,
      types: self.selectedType.value.length ? self.selectedType.value : undefined,
      base_client_skill: self.selectedBaseStatus.value !== null && self.selectedBaseStatus.value !== '' ? self.selectedBaseStatus.value : undefined,
      has_descriptors: self.selectedDescriptorStatus.value !== null && self.selectedDescriptorStatus.value !== '' ? String(self.selectedDescriptorStatus.value) : undefined,
      has_proficiencies: self.selectedProficiencyStatus.value !== null && self.selectedProficiencyStatus.value !== '' ? String(self.selectedProficiencyStatus.value) : undefined
    };
  }

  function _triggerDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // 4. Methods on self
  self.fetchData = async (notify) => {
    self.loading.value = true;
    try {
      const resp = await axios.post("skills/baseSkillsAdmin", {
        page: self.currentPage.value,
        pageSize: self.pageSize.value,
        sortBy: SORT_COLUMN_MAP[self.sortBy.value] || 'name',
        sortDesc: self.sortDesc.value,
        ..._buildFilterParams()
      });
      const result = resp.data;
      self.items.value = result.items || [];
      self.totalItems.value = parseInt(result.total) || 0;
      if (result.types) self.types.value = result.types;
    } catch (error) {
      console.error('Error fetching base skills admin data', error);
      notify?.('Error loading data', 'error');
    } finally {
      self.loading.value = false;
    }
  };

  self.fetchProviders = async (notify) => {
    try {
      const resp = await axios.get("skills/frameworks");
      self.providers.value = resp.data || [];
    } catch (error) {
      console.error('Error fetching providers', error);
      notify?.('Error loading providers', 'error');
    }
  };

  self.toggleBaseSkill = async (item, notify) => {
    const newVal = item.base_client_skill ? 0 : 1;
    try {
      await axios.post("skills/updateBaseClientSkill", {
        ids: [item.framework_provider_data_id],
        base_client_skill: newVal
      });
      item.base_client_skill = newVal;
    } catch (error) {
      console.error('Error toggling base skill', error);
      notify?.('Error updating skill', 'error');
    }
  };

  self.bulkSetBase = async (val, notify) => {
    const ids = self.selected.value.map(s => s.framework_provider_data_id);
    if (!ids.length) return;
    try {
      await axios.post("skills/updateBaseClientSkill", {
        ids,
        base_client_skill: val
      });
      self.selected.value = [];
      notify?.(`Updated ${ids.length} skill(s)`, 'success');
      await self.fetchData(notify);
    } catch (error) {
      console.error('Error bulk updating', error);
      notify?.('Error updating skills', 'error');
    }
  };

  self.openDetailDialog = async (item, notify) => {
    self.detailDialog.show = true;
    self.detailDialog.loading = true;
    self.detailDialog.item = item;
    self.detailDialog.descriptors = [];
    self.detailDialog.proficiencies = [];
    try {
      const resp = await axios.post("skills/skillDescriptorDetail", {
        framework_provider_id: item.framework_provider_id,
        external_id: item.external_id
      });
      self.detailDialog.descriptors = resp.data.descriptors || [];
      self.detailDialog.proficiencies = resp.data.proficiencies || [];
    } catch (error) {
      console.error('Error fetching descriptor detail', error);
      notify?.('Error loading detail', 'error');
    } finally {
      self.detailDialog.loading = false;
    }
  };

  self.closeDetailDialog = () => {
    self.detailDialog.show = false;
  };

  self.exportToExcel = async (notify) => {
    self.exporting.value = true;
    try {
      const resp = await axios.post("skills/exportSkillsExcel",
        _buildFilterParams(),
        { responseType: 'blob' }
      );
      _triggerDownload(new Blob([resp.data]), 'skills_export.xlsx');
    } catch (error) {
      console.error('Error exporting', error);
      notify?.('Error exporting to Excel', 'error');
    } finally {
      self.exporting.value = false;
    }
  };

  self.onSearch = (notify) => {
    self.currentPage.value = 1;
    self.fetchData(notify);
  };

  self.onFilterChange = (notify) => {
    self.currentPage.value = 1;
    self.fetchData(notify);
  };

  self.onPageNav = (page) => {
    self.currentPage.value = page;
  };

  self.onSort = (column) => {
    if (self.sortBy.value === column) {
      self.sortDesc.value = !self.sortDesc.value;
    } else {
      self.sortBy.value = column;
      self.sortDesc.value = false;
    }
  };

  self.parseLangs = (csv) => {
    if (!csv) return [];
    return csv.split(',').map(s => s.trim()).filter(Boolean);
  };

  const PROFICIENCY_ORDER = ['Basic', 'Intermediate', 'Advanced', 'Expert', 'Strategic'];

  self.proficiencyLevelIndex = (name) => {
    const idx = PROFICIENCY_ORDER.indexOf(name);
    return idx === -1 ? 999 : idx;
  };

  self.groupProficienciesByLang = (proficiencies) => {
    const order = PROFICIENCY_ORDER;
    const map = {};
    proficiencies.forEach(p => {
      if (!map[p.language_code]) map[p.language_code] = [];
      map[p.language_code].push(p);
    });
    for (const lang of Object.keys(map)) {
      map[lang].sort((a, b) => {
        const aIdx = order.indexOf(a.proficiency);
        const bIdx = order.indexOf(b.proficiency);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      });
    }
    return map;
  };

  return self;
}
